import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { isValidEmail } from '../../lib/compose';
import { getInitials, getAvatarColor } from '../../lib/avatar';

const sameEmail = (a, b) => a.toLowerCase() === b.toLowerCase();

// Çipli alıcı alanı (Kime / Bilgi / Gizli). Girilen metin üstte tutulur (inputValue),
// böylece gönderirken henüz çipe dönmemiş geçerli adres de alıcılara katılabilir.
export const RecipientField = ({
  label,
  recipients,
  onChange,
  inputValue,
  onInputChange,
  contacts = [],
  rightSlot = null,
  autoFocus = false,
  testId,
  placeholder = 'alici@ornek.com',
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [invalid, setInvalid] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const query = inputValue.trim().toLowerCase();
  const suggestions = query
    ? contacts
        .filter(
          (c) =>
            (c.name.toLowerCase().includes(query) || c.email.toLowerCase().includes(query)) &&
            !recipients.some((r) => sameEmail(r.email, c.email)),
        )
        .slice(0, 5)
    : [];
  const showSuggestions = open && suggestions.length > 0;

  const addRecipients = (list) => {
    const next = [...recipients];
    list.forEach((item) => {
      if (!next.some((r) => sameEmail(r.email, item.email))) next.push(item);
    });
    onChange(next);
  };

  const pickContact = (contact) => {
    addRecipients([{ name: contact.name, email: contact.email }]);
    onInputChange('');
    setOpen(false);
    setInvalid(false);
  };

  // Yazılan metindeki geçerli adresleri çipe çevir; geçersizler girişte kalır
  const commitText = () => {
    const tokens = inputValue.split(/[,;\s]+/).filter(Boolean);
    if (tokens.length === 0) return false;
    const valid = tokens.filter(isValidEmail);
    const rest = tokens.filter((t) => !isValidEmail(t));
    if (valid.length) addRecipients(valid.map((email) => ({ name: email, email })));
    onInputChange(rest.join(' '));
    setInvalid(rest.length > 0);
    return valid.length > 0;
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setHighlight((h) => (e.key === 'ArrowDown' ? (h + 1) % suggestions.length : (h - 1 + suggestions.length) % suggestions.length));
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      if (!inputValue.trim()) return; // boşken Tab normal odak geçişi yapsın
      e.preventDefault();
      const typedValid = inputValue.split(/[,;\s]+/).filter(Boolean).every(isValidEmail);
      if (showSuggestions && !typedValid) pickContact(suggestions[Math.min(highlight, suggestions.length - 1)]);
      else commitText();
      return;
    }
    if (e.key === 'Backspace' && !inputValue && recipients.length > 0) {
      onChange(recipients.slice(0, -1));
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    const tokens = text.split(/[,;\s]+/).filter(Boolean);
    if (tokens.length > 1) {
      e.preventDefault();
      const valid = tokens.filter(isValidEmail);
      if (valid.length) addRecipients(valid.map((email) => ({ name: email, email })));
      const rest = tokens.filter((t) => !isValidEmail(t));
      onInputChange(rest.join(' '));
      setInvalid(rest.length > 0);
    }
  };

  return (
    <div className="flex items-start gap-3 border-b border-border" ref={wrapRef}>
      <span className="w-14 shrink-0 pt-[13px] text-sm text-muted-foreground">{label}</span>
      <div className="relative flex-1 min-w-0 py-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {recipients.map((r) => {
            const hasName = r.name && !sameEmail(r.name, r.email);
            return (
              <span
                key={r.email}
                className="inline-flex items-center gap-2 max-w-full pl-1 pr-2 py-1 rounded-full bg-accent text-sm"
                data-testid={`${testId}-chip`}
              >
                <span className={`h-6 w-6 shrink-0 rounded-full text-[10px] font-semibold flex items-center justify-center ${getAvatarColor(r.email)}`}>
                  {getInitials(hasName ? r.name : '', r.email)}
                </span>
                <span className="truncate">
                  {hasName ? (
                    <>
                      <span className="font-medium">{r.name}</span>{' '}
                      <span className="text-muted-foreground">&lt;{r.email}&gt;</span>
                    </>
                  ) : (
                    r.email
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => onChange(recipients.filter((x) => x.email !== r.email))}
                  aria-label={`${r.email} adresini kaldır`}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              onInputChange(e.target.value);
              setInvalid(false);
              setOpen(true);
              setHighlight(0);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setOpen(true)}
            onBlur={() => { if (inputValue.trim()) commitText(); }}
            placeholder={recipients.length === 0 ? placeholder : ''}
            aria-label={label}
            aria-invalid={invalid}
            className={`flex-1 min-w-[160px] h-9 bg-transparent text-sm outline-none ${invalid ? 'text-destructive' : ''}`}
            data-testid={`${testId}-input`}
          />
        </div>

        {showSuggestions && (
          <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg overflow-hidden" role="listbox">
            {suggestions.map((c, i) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickContact(c)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${i === highlight ? 'bg-accent' : 'hover:bg-accent/60'}`}
                data-testid={`suggestion-${c.id}`}
              >
                <span className={`h-8 w-8 shrink-0 rounded-full text-xs font-semibold flex items-center justify-center ${getAvatarColor(c.email)}`}>
                  {getInitials(c.name, c.email)}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">{c.name}</span>
                  <span className="block text-xs text-muted-foreground truncate">{c.email}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {rightSlot && <div className="shrink-0 pt-[11px] flex items-center gap-3 text-sm">{rightSlot}</div>}
    </div>
  );
};

export default RecipientField;
